"use client";

import React, { useState, useEffect } from "react";
import { createRoot, Root } from "react-dom/client";

// Toast component handles fade in/out
interface ToastProps {
  message: string;
  duration?: number;
  onRemove: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, duration = 3000, onRemove }) => {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLeaving(true), duration);
    return () => clearTimeout(timer);
  }, [duration]);

  // Remove after fade-out
  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => onRemove(), 500); // fade-out duration
    return () => clearTimeout(timer);
  }, [leaving, onRemove]);

  return (
    <div className={`toast-card ${leaving ? "toast-leave" : "toast-enter"}`}>
      <p className="toast-message">{message}</p>
      <button className="close-btn toast-close-btn" onClick={() => setLeaving(true)}>
        ✕
      </button>
    </div>
  );
};

// Toast manager
interface ToastData {
  id: number;
  message: string;
  duration?: number;
}

const toasts: ToastData[] = [];
let container: HTMLElement | null = null;
let root: Root | null = null;

function getRoot(): Root {
  if (!container) {
    container = document.createElement("div");
    container.className = "toast-container-wrapper";
    document.body.appendChild(container);
    root = createRoot(container);
  }
  return root!;
}

function renderToasts() {
  const r = getRoot();
  r.render(
    <>
      {toasts.map((t) => (
        <Toast
          key={t.id}
          message={t.message}
          duration={t.duration}
          onRemove={() => removeToast(t.id)}
        />
      ))}
    </>
  );
}

function removeToast(id: number) {
  const index = toasts.findIndex((t) => t.id === id);
  if (index !== -1) {
    toasts.splice(index, 1);
    renderToasts();
  }
}

// The function you call from anywhere
export function showToast(message: string, duration = 3000) {
  if (typeof window === "undefined") return;
  const id = Date.now() + Math.random();
  toasts.push({ id, message, duration });
  renderToasts();
}
